import './login';
import { request, history } from 'umi';
import { noLoginProcess } from '@/apis/login';
import { apiHostMain } from '@/apis/hosts';
import { errorHandlerMain } from '@/apis/handler';
import { createSu, getToken, getTestUser, parseTextResponseData } from '@/apis/util';
import camelcaseKeys from 'camelcase-keys';
import { MessageError } from '@/components';
import { debounce, delay } from 'lodash-es';
import type { RequestOptionsInit, ResponseError } from 'umi-request';
import type { I } from '@/types/project';

type DataInfoType = {
  data: I.ResponseStructure<any>;
  showError: boolean;
};

export const debounceMessage = debounce((msg: string) => {
  MessageError(msg);
}, 300);

// 请求返回 ret !== 0 的处理
const businessMessageHandle = (dataInfo: DataInfoType) => {
  const { data, showError = true } = dataInfo;
  const { ret, msg } = data || {};

  if (ret === 10003) {
    debounceMessage(msg || '登录已过期，请重新登录');
    delay(() => {
      console.log('跳转登录');
      // cb 存储当前 href, 用于登录后回跳
      noLoginProcess();
    }, 1000);
    return;
  }

  if (ret === 3) {
    MessageError(msg || '抱歉，您没有权限访问该页面');
    delay(() => {
      history.goBack();
    }, 1000);
    return;
  }

  if (showError) {
    MessageError(msg || '没有返回提示信息');
  }
};

// 处理请求返回的数据
const dealwithResponseData = (
  responseData: any,
  camelcase: boolean,
  showError: boolean
) => {
  // 将 ResposeType: text 类型中 大数 转化为字符串
  const { data, response } = responseData;
  const { headers } = response;

  let resData = data;
  if ((headers.get('Content-Type') || '').indexOf('application/text') !== -1) {
    resData = parseTextResponseData(data);
  }

  if (resData.ret === 0) {
    return camelcase ? camelcaseKeys(resData, { deep: true }) : resData;
  }

  // 业务请求信息提示
  businessMessageHandle({ data: resData, showError });
  const errorInfo = { data: resData };

  // [3、10003] 不抛出异常，避免 watchdog 频繁收集 unhandledrejection 日志，其他r et 异常上报
  if ([3, 10003].includes(resData.ret)) {
    // Promise的结果被"忽略"，并且后续的then或catch方法也不会被调用，
    return new Promise(() => {});
  }

  return Promise.reject(errorInfo);
};

/**
 * 历史接口第一套封装
 * 后端接口格式要求:https://epai.huolala.cn/index.php?_m=xxxxx
 */
const requestMainWrap = (token: string) => {
  return (
    url: string,
    payload: { showError?: boolean } & RequestOptionsInit = {},
    camelcase = false
  ) => {
    const params = {
      ...payload.params, // 上层有传递 _su  则使用上层传递过来的，主要原因在于上层可能会使用到 _su 加密或者其它操作
      _su: createSu(),
    };

    let showError = true;
    if (payload.showError === false) {
      showError = false;
    }

    return request(url, {
      prefix: `${apiHostMain}/index.php`,
      errorHandler: (err: ResponseError) => {
        errorHandlerMain(err);
      },
      getResponse: true,
      ...payload,
      params,
      headers: {
        Token: token,
        'x-test-user': getTestUser(),
        ...payload.headers,
      },
      // eslint-disable-next-line consistent-return
    }).then((res) => {
      return dealwithResponseData(res, camelcase, showError);
    });
  };
};

/**
 * 历史接口第二套封装
 * 后端接口格式要求：http://eapi.huolala.cn/${serviceType}Api?name=xxxxxx
 * 例如:
 *  eoc服务 order.porterageConfig 接口
 *  http://eapi.huolala.cn/eocApi?name=order.porterageConfig
 *  header头要求：hll-appid: bfe-efc-svc
 *  data: {
 *    name: 'order.porterageConfig',
 *    data: { xxx }
 *  }
 */
const requestCommonWrap = (token: string) => {
  return (
    payload: { showError?: boolean } & RequestOptionsInit = {},
    camelcase = false,
    serviceType?: string
  ) => {
    const su = createSu();

    const payloadParams = {
      // 如果payload.params中定义了name，这里的name会被覆盖掉
      name: (payload as any).data?.name,
      os: 'ep',
      su,
      ...payload?.params,
      _su: su,
    };

    const payloadData = {
      _su: su,
      ...payload?.data,
      data: {
        _su: su,
        os: 'ep',
        ...payload?.data?.data,
      },
    };

    let showError = true;
    if (payload.showError === false) {
      showError = false;
    }
    const Headers: any = {
      Token: token,
      'x-test-user': getTestUser(),
      'Content-Type': 'application/json',
      ...payload.headers,
    };
    if (serviceType) {
      Headers['hll-appid'] = `bfe-${serviceType}-svc`;
    }
    return request('', {
      prefix: `${apiHostMain}${serviceType ? `/${serviceType}Api` : ''}`,
      errorHandler: (err: ResponseError) => {
        errorHandlerMain(err);
      },
      getResponse: true,
      ...payload,
      data: payloadData,
      params: payloadParams,
      body: JSON.stringify(payloadParams),
      headers: Headers,
      // eslint-disable-next-line consistent-return
    }).then((res) => {
      return dealwithResponseData(res, camelcase, showError);
    });
  };
};

/**
 * 第三套接口封装
 * 后端最新的接口格式要求：https://eapi.huolala.cn/${serviceType}CoreSoa/xxxx/xxxxxx
 * 例如：eoc服务的/bargain/agree接口
 *  https://eapi.huolala.cn/eocCoreSoa/bargain/agree
 *  data: { xxxxxxx }
 */
const requestBaseWrap = (token: string) => {
  return (
    url: string,
    payload: { showError?: boolean } & RequestOptionsInit = {},
    camelcase = false
  ) => {
    const params = {
      ...payload.params, // 上层有传递 _su  则使用上层传递过来的，主要原因在于上层可能会使用到 _su 加密或者其它操作
      _su: createSu(),
    };

    let showError = true;
    if (payload.showError === false) {
      showError = false;
    }

    return request(url, {
      prefix: `${apiHostMain}`,
      errorHandler: (err: ResponseError) => {
        errorHandlerMain(err);
      },
      getResponse: true,
      ...payload,
      params,
      headers: {
        Token: token,
        'x-test-user': getTestUser(),
        'Content-Type': 'application/json',
        ...payload.headers,
      },
      // eslint-disable-next-line consistent-return
    }).then((res) => {
      return dealwithResponseData(res, camelcase, showError);
    });
  };
};
export const requestMain = requestMainWrap(getToken());

export const requestCommon = requestCommonWrap(getToken());

export const requestBase = requestBaseWrap(getToken());
